import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { call } from '@/api/desk';
import {
  DataTable, Filters, FormModal, KV, Modal, Notice, PageHeader, Pager,
  Search, Sel, arr, dOnly, errMsg, money, useAction, useGet, useList, type FieldDef,
} from '@/features/shared/ops/kit';
import { badge } from '../lib';

const STATUSES = ['requested', 'awaiting_payment', 'paid', 'processing', 'ready', 'delivered', 'rejected'];
const TYPES = ['full_transcript', 'term_result', 'certificate', 'attestation', 'other'];

const label = (s: string) =>
  (s ?? '').replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

function studentName(r: any): string {
  const st = r?.alumni?.student;
  const from = [st?.first_name, st?.last_name].filter(Boolean).join(' ');
  const fallback = `Alumni #${r?.alumni_id ?? '—'}`;
  return r?.student_name ?? r?.alumni_name ?? from ?? fallback;
}

function admissionNo(r: any): string {
  return r?.alumni?.student?.admission_no ?? r?.admission_no ?? '—';
}

const can = {
  mark_paid: (s: string) => s === 'awaiting_payment',
  start:     (s: string) => s === 'requested' || s === 'paid',
  ready:     (s: string) => ['requested', 'paid', 'processing'].includes(s),
  reject:    (s: string) => ['requested', 'awaiting_payment', 'paid', 'processing'].includes(s),
  deliver:   (s: string) => s === 'ready',
};

/* ---------- detail + actions modal ---------- */
function RequestDetail({ id, onClose, onChanged }: { id: number; onClose: () => void; onChanged: () => void }) {
  const qc = useQueryClient();
  const q = useGet<any>('desk-alumni-request', `/school/alumni-requests/${id}`);
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['desk-alumni-request'] });
    qc.invalidateQueries({ queryKey: ['desk-alumni-requests'] });
    onChanged();
  };
  const a = useAction(refresh);
  const [deliverOpen, setDeliverOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const r = q.data;
  const st = r?.status as string | undefined;

  const process = (action: 'mark_paid' | 'start' | 'ready', okMsg: string) =>
    a.run(
      () => call('post', `/school/alumni-requests/${id}/process`, { body: { action } }),
      { ok: okMsg }
    );

  const deliverFields: FieldDef[] = [
    { name: 'delivered_via', label: 'Delivered via', type: 'select', required: true, options: [
      { value: 'pickup', label: 'Pickup at school' },
      { value: 'email', label: 'Email to alumnus' },
      { value: 'courier', label: 'Courier' },
      { value: 'post', label: 'Post / mail' },
    ]},
    { name: 'note', label: 'Note (optional)', type: 'textarea', maxLength: 255 },
  ];

  const rejectFields: FieldDef[] = [
    { name: 'note', label: 'Reason for rejection', type: 'textarea', required: true, maxLength: 255 },
  ];

  return (
    <Modal title={r ? `${studentName(r)} · ${label(r.type)}` : 'Transcript request'} onClose={onClose} wide>
      {q.isLoading && <p className="text-sm text-slate-500 py-4 text-center">Loading…</p>}
      {q.error && <Notice kind="error">{errMsg(q.error)}</Notice>}
      {a.banner}
      {r && (
        <div className="space-y-4 text-sm">
          <div className="flex flex-wrap gap-2">
            {can.mark_paid(st ?? '') && <Button variant="ghost" onClick={() => process('mark_paid', 'Marked as paid.')}>Mark as paid</Button>}
            {can.start(st ?? '') && <Button variant="ghost" onClick={() => process('start', 'Processing started.')}>Start processing</Button>}
            {can.ready(st ?? '') && <Button variant="ghost" onClick={() => process('ready', 'Marked ready.')}>Mark ready</Button>}
            {can.reject(st ?? '') && <Button variant="ghost" onClick={() => setRejectOpen(true)}>Reject</Button>}
            {can.deliver(st ?? '') && <Button onClick={() => setDeliverOpen(true)}>Deliver</Button>}
          </div>

          <div className="grid md:grid-cols-2 gap-x-6">
            <div>
              <KV k="Status" v={badge(st ?? '—')} />
              <KV k="Type" v={label(r.type)} />
              <KV k="Copies" v={r.copies ?? '1'} />
              <KV k="Fee" v={money(r.fee_amount)} />
              <KV k="Payment" v={r.invoice_id ? 'Invoiced' : (r.status === 'awaiting_payment' ? 'Awaiting payment' : '—')} />
              <KV k="Submitted" v={dOnly(r.created_at)} />
            </div>
            <div>
              <KV k="Alumnus" v={studentName(r)} />
              <KV k="Admission no." v={admissionNo(r)} />
              {r.alumni?.student?.email && <KV k="Email" v={r.alumni.student.email} />}
              {r.alumni?.student?.phone && <KV k="Phone" v={r.alumni.student.phone} />}
              {r.delivered_via && <KV k="Delivered via" v={label(r.delivered_via)} />}
              {r.processed_by?.name && <KV k="Last action by" v={r.processed_by.name} />}
            </div>
          </div>
        </div>
      )}

      {deliverOpen && (
        <FormModal
          title="Deliver transcript"
          fields={deliverFields}
          onClose={() => setDeliverOpen(false)}
          onSubmit={async (v) => {
            const res = await call('post', `/school/alumni-requests/${id}/deliver`, { body: v });
            a.setMsg(res.message ?? 'Marked as delivered.');
            setDeliverOpen(false);
            refresh();
          }}
        />
      )}

      {rejectOpen && (
        <FormModal
          title="Reject request"
          fields={rejectFields}
          submitLabel="Reject"
          onClose={() => setRejectOpen(false)}
          onSubmit={async (v) => {
            const res = await call('post', `/school/alumni-requests/${id}/process`, { body: { action: 'reject', note: v.note } });
            a.setMsg(res.message ?? 'Request rejected.');
            setRejectOpen(false);
            refresh();
          }}
        />
      )}
    </Modal>
  );
}

/* ---------- list ---------- */
function Requests() {
  const qc = useQueryClient();
  const [f, setF] = useState({ q: '', status: '', type: '' });
  const list = useList('desk-alumni-requests', '/school/alumni-requests', f);
  const [open, setOpen] = useState<number | null>(null);
  const set = (k: keyof typeof f) => (v: string) => setF((x) => ({ ...x, [k]: v }));

  // arr is not generic — cast the return type
  const rows = arr(list.rows) as any[];
  const filtered = f.q
    ? rows.filter((r: any) => {
        const n = studentName(r).toLowerCase();
        const ref = String(r.id);
        const q = f.q.toLowerCase();
        return n.includes(q) || ref.includes(q);
      })
    : rows;

  return (
    <Card title="Transcript requests">
      <Filters>
        <Search value={f.q} onChange={set('q')} placeholder="Name or request #" />
        <Sel value={f.status} onChange={set('status')} placeholder="Any status"
          options={STATUSES.map((s) => ({ value: s, label: label(s) }))} />
        <Sel value={f.type} onChange={set('type')} placeholder="Any type"
          options={TYPES.map((t) => ({ value: t, label: label(t) }))} />
      </Filters>

      <DataTable
        loading={list.isLoading}
        error={list.error}
        rows={filtered}
        empty="No transcript requests yet."
        onRow={(r: any) => setOpen(r.id)}
        cols={[
          {
            head: 'Requested by',
            cell: (r: any) => (
              <span>
                <span className="block">{studentName(r)}</span>
                <span className="block text-xs text-slate-500">{admissionNo(r)}</span>
              </span>
            ),
          },
          { head: 'Type', cell: (r: any) => label(r.type) },
          { head: 'Copies', right: true, cell: (r: any) => r.copies ?? 1 },
          { head: 'Fee', right: true, cell: (r: any) => money(r.fee_amount) },
          {
            head: 'Payment',
            cell: (r: any) => (r.status === 'awaiting_payment'
              ? <span className="text-amber-700">Awaiting payment</span>
              : r.status === 'requested'
                ? <span className="text-slate-500">Free</span>
                : <span className="text-green-700">Paid</span>),
          },
          { head: 'Submitted', cell: (r: any) => dOnly(r.created_at) },
          { head: 'Status', cell: (r: any) => badge(r.status) },
        ]}
      />
      <Pager meta={list.meta} page={list.page} onPage={list.setPage} />

      {open != null && (
        <RequestDetail
          id={open}
          onClose={() => setOpen(null)}
          onChanged={() => qc.invalidateQueries({ queryKey: ['desk-alumni-requests'] })}
        />
      )}
    </Card>
  );
}

export function TranscriptRequestsPage() {
  return (
    <div>
      <PageHeader
        title="Transcript requests"
        sub="Alumni request official and unofficial transcripts — the school sets the fee"
      />
      <Requests />
    </div>
  );
}
